/**
 * FILTER MEMORY — owner rulings 2026-10-16, "filters persist across the app":
 *
 *   1. Each page remembers its own filters, sort, tab and view.
 *   2. Remembered ON THE ACCOUNT until cleared (survives a browser close and
 *      follows the person to other devices).
 *   3. A link that names its own filters WINS and is shown exactly; memory
 *      comes back the next time the page is opened without such params.
 *   4. Saved filters (`useSavedFilters`) stay as named starting points and
 *      apply only when nothing is remembered.
 *
 * ## The three halves
 *
 * - **URL state** — the rules below, one per list pathname, naming the params
 *   to keep. `<FilterMemory>` (`components/FilterMemory.tsx`, mounted once, in
 *   `Shell` in `App.tsx`, around the page) restores
 *   them on ARRIVAL at the pathname BEFORE the page renders, and stores them
 *   whenever the page changes them. A restore happens only when the address
 *   carries NO params other than `page`/`perPage`; anything else is a link,
 *   shown exactly and read-only for memory. See `use-filter-memory.ts`.
 * - **Table state** — `useTableFilters` remembers its own funnels and sort,
 *   keyed by pathname and column set, or by `options.memoryId` when the
 *   column set can change (`useRememberedTableState`).
 * - **Plain component state** — `useRememberedState(id, initial)` for search
 *   boxes and sorts a page keeps in `useState`.
 *
 * Owner 2026-10-23, "the sort order should get stored in the preference store
 * and persist": every sort persists on the account. Where a page keeps its sort
 * in the URL (`/tasks`, `/projects`, `/products`, `/cycles`, `/intakes`,
 * `/audit-log`: params `sort` + `order`) the rule keeps both; everywhere else
 * the sort is table or plain state. Three refinements of the rulings below:
 *
 *   - A LINK that names funnels but no `sort`/`order` still gets the
 *     remembered TABLE SORT (`mayRestoreSort`); ruling 3 governs only what the
 *     link names. Its funnels are still shown exactly.
 *   - Every page (not only ruled ones) waits for the account read on arrival,
 *     and state that lands later still reaches an untouched table or value.
 *   - While acting as another user (support session) THEIR memory is restored
 *     and nothing is written (`store.ts`).
 *
 * Owner 2026-10-16, "every sort and filter persists across the app": table and
 * plain state work on RECORD pages too, keyed on the record TYPE's template
 * path (`stateMemoryPath`: `/companies/782` → `/companies/record`), so one
 * choice is shared by every record of that type. Record pages get no URL
 * memory, and their state memory ignores the link rule (ruling 3): a record
 * page's params (`?create=quote`, `?tab=…`) are one-shot actions, never the
 * state these hooks hold, so record-page state is always restored.
 *
 * Stored through `./store.ts`: URL state under the preferences key
 * `filterMemory` (server-validated; keys are exact lowercase pathnames), table
 * and component state under `filterMemoryState`. `''` is a remembered CLEAR.
 *
 * ## Ordering against the other arrival writers
 *
 * `<FilterMemory>` renders a skeleton for a ruled page until the restore
 * decision is made and applied, so the page mounts with the remembered URL
 * already in place. When memory answered (`hasFilterMemory`),
 * `useSavedFilters` settles as `'remembered'` (no saved set applied, no saves),
 * and `useDefaultAccountLeadFilter`, `/projects`' and `/sales-tracker`'s
 * current-cycle defaults stand down — so a remembered clear stays cleared.
 */
import type { FilterMemoryRule } from './types';
import { FILTER_MEMORY_ADMIN_MARKETING } from './admin-marketing';
import { FILTER_MEMORY_CATALOG } from './catalog';
import { FILTER_MEMORY_DASHBOARDS } from './dashboards';
import { FILTER_MEMORY_DEALS_QUOTES } from './records-deals-quotes';
import { FILTER_MEMORY_INVOICES_CREDIT } from './records-invoices-credit';
import { FILTER_MEMORY_COMPANIES } from './records-companies';
import { FILTER_MEMORY_PRODUCT_INSIGHTS } from './product-insights';
import { FILTER_MEMORY_TASKS_ENQUIRIES } from './tasks-enquiries';
import { FILTER_MEMORY_TRACKERS } from './trackers';

export type { FilterMemoryRule } from './types';

export const FILTER_MEMORY_RULES: readonly FilterMemoryRule[] = [
  ...FILTER_MEMORY_COMPANIES,
  ...FILTER_MEMORY_DEALS_QUOTES,
  ...FILTER_MEMORY_INVOICES_CREDIT,
  ...FILTER_MEMORY_CATALOG,
  ...FILTER_MEMORY_TRACKERS,
  ...FILTER_MEMORY_TASKS_ENQUIRIES,
  ...FILTER_MEMORY_DASHBOARDS,
  ...FILTER_MEMORY_PRODUCT_INSIGHTS,
  ...FILTER_MEMORY_ADMIN_MARKETING,
];

const RULES_BY_PATH = new Map<string, FilterMemoryRule>();
for (const rule of FILTER_MEMORY_RULES) {
  const existing = RULES_BY_PATH.get(rule.path);
  // Two group files naming one path: keep the union rather than letting the
  // later file silently drop the earlier one's keys.
  RULES_BY_PATH.set(
    rule.path,
    existing
      ? { path: rule.path, keep: Array.from(new Set([...existing.keep, ...rule.keep])) }
      : rule,
  );
}

/** `/invoices/` and `/invoices` are one page. */
export function normaliseMemoryPath(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.replace(/\/+$/, '') : pathname;
}

/** The rule for this EXACT pathname, or `null`. Detail pages never inherit one. */
export function filterMemoryRuleFor(pathname: string): FilterMemoryRule | null {
  return RULES_BY_PATH.get(normaliseMemoryPath(pathname)) ?? null;
}

/** A path segment that names one record: a numeric id or a uuid/hex token. */
function isRecordSegment(segment: string): boolean {
  return /^\d+$/.test(segment) || /^[0-9a-f-]{16,}$/i.test(segment);
}

/**
 * Is this a record page (`/invoices/123`, `/marketing/lists/<uuid>`)? Record
 * pages get no URL memory (they carry no rule, and an id is not a filter).
 */
export function isRecordPath(pathname: string): boolean {
  return normaliseMemoryPath(pathname).split('/').some(isRecordSegment);
}

/** What a record segment becomes in a state key. */
export const RECORD_SEGMENT = 'record';

/**
 * The pathname table and plain-state memory are keyed on: every record
 * segment becomes `record` (`/companies/782` → `/companies/record`,
 * `/marketing/campaigns/<uuid>/edit` → `/marketing/campaigns/record/edit`).
 *
 * Owner 2026-10-16: a sort or filter chosen on one record stays for every
 * record of that type, so there is ONE entry per record TYPE, never per
 * record, and the preferences document stays bounded. List pathnames come
 * back unchanged.
 */
export function stateMemoryPath(pathname: string): string {
  return normaliseMemoryPath(pathname)
    .split('/')
    .map((segment) => (isRecordSegment(segment) ? RECORD_SEGMENT : segment))
    .join('/');
}

/**
 * The kept params of `params`, canonical: kept keys only, sorted by key (value
 * order within a key preserved), `page` never. `''` when none is present.
 */
export function keptQuery(params: URLSearchParams, keep: readonly string[]): string {
  const out = new URLSearchParams();
  for (const key of [...keep].sort()) {
    if (key === 'page') continue;
    for (const value of params.getAll(key)) out.append(key, value);
  }
  return out.toString();
}

/* ── Current-cycle markers (owner ruling 2026-10-16) ─────────────────────── */

export const CURRENT_CYCLE_ID = '@current';
export const CURRENT_CYCLE_NAME = '@current-name';

/** A param that names a cycle: `cycleId`, `cycle`, `capacityCycleId`, `rcCycle`… */
export function isCycleKey(key: string): boolean {
  return /cycle/i.test(key);
}

interface CycleRef {
  id: number;
  cycleName: string;
}

/** Rewrite a cycle param's value, token by token (CSV lists included). */
function mapCycleTokens(value: string, map: (token: string) => string | null): string {
  return value
    .split(',')
    .map(map)
    .filter((token): token is string => token !== null && token !== '')
    .join(',');
}

/**
 * Before storing: the then-current cycle becomes a marker, so it follows the
 * next current cycle after rollover. Any other cycle is stored as picked.
 */
export function encodeCycleMarkers(query: string, current: CycleRef | null): string {
  if (query === '' || current === null) return query;
  const params = new URLSearchParams(query);
  const out = new URLSearchParams();
  for (const [key, value] of params) {
    if (!isCycleKey(key)) {
      out.append(key, value);
      continue;
    }
    out.append(
      key,
      mapCycleTokens(value, (token) =>
        token === String(current.id)
          ? CURRENT_CYCLE_ID
          : token === current.cycleName
            ? CURRENT_CYCLE_NAME
            : token,
      ),
    );
  }
  return out.toString();
}

/** Does a stored query hold a marker that needs the current cycle to resolve? */
export function hasCycleMarker(query: string): boolean {
  return query.includes(encodeURIComponent(CURRENT_CYCLE_ID)) || query.includes(CURRENT_CYCLE_ID);
}

/**
 * On restore: markers become today's current cycle. With no current cycle
 * (between business years, or `/cycles` unreadable) the marker is dropped, and
 * a key left empty is dropped with it — never a guessed cycle.
 */
export function decodeCycleMarkers(query: string, current: CycleRef | null): string {
  if (query === '' || !hasCycleMarker(query)) return query;
  const params = new URLSearchParams(query);
  const out = new URLSearchParams();
  for (const [key, value] of params) {
    if (!isCycleKey(key)) {
      out.append(key, value);
      continue;
    }
    const next = mapCycleTokens(value, (token) => {
      if (token === CURRENT_CYCLE_ID) return current ? String(current.id) : null;
      if (token === CURRENT_CYCLE_NAME) return current ? current.cycleName : null;
      return token;
    });
    if (next !== '' || value === '') out.append(key, next);
  }
  return out.toString();
}

export function namesAnyKept(params: URLSearchParams, keep: readonly string[]): boolean {
  return keep.some((key) => key !== 'page' && params.has(key));
}
